import React, { useState, useEffect, useRef } from 'react';
import { MdMergeType, MdExpandMore, MdExpandLess } from 'react-icons/md';

const UnionForm = ({ erdState, currentUnion, setCurrentUnion, editingMode, setEditingMode, onSaveEvent }) => {
  const [expandedSuperCategories, setExpandedSuperCategories] = useState({});
  const [pendingSaveEvent, setPendingSaveEvent] = useState(null);
  const inputTextRef = useRef(erdState.inputText);

  // Toggle individual super-category expansion
  const toggleSuperCategoryExpansion = (index) => {
    setExpandedSuperCategories(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Add super-category
  const addSuperCategory = () => {
    setCurrentUnion(prev => ({
      ...prev,
      superCategories: ['', ...prev.superCategories]
    }));

    // Shift all existing expansion indices up by 1
    setExpandedSuperCategories(prev => {
      const newState = {};
      Object.keys(prev).forEach(key => {
        const numKey = parseInt(key);
        newState[numKey + 1] = prev[numKey];
      });
      // Auto-expand new super-category at index 0
      newState[0] = true;
      return newState;
    });
  };

  // Remove super-category
  const removeSuperCategory = (index) => {
    setCurrentUnion(prev => ({
      ...prev,
      superCategories: prev.superCategories.filter((_, i) => i !== index)
    }));

    // Clean up expansion state
    setExpandedSuperCategories(prev => {
      const newState = { ...prev };
      delete newState[index];
      Object.keys(newState).forEach(key => {
        const numKey = parseInt(key);
        if (numKey > index) {
          newState[numKey - 1] = newState[numKey];
          delete newState[numKey];
        }
      });
      return newState;
    });
  };

  // Update super-category
  const updateSuperCategory = (index, value) => {
    setCurrentUnion(prev => ({
      ...prev,
      superCategories: prev.superCategories.map((category, i) =>
        i === index ? value : category
      )
    }));
  };

  // Generate or update union notation
  const generateUnionNotation = () => {
    if (!currentUnion.subCategoryEntity.trim()) {
      alert('Please enter a sub-category entity name');
      return;
    }

    const validSuperCategories = currentUnion.superCategories.filter(category => category.trim() !== '');
    if (validSuperCategories.length === 0) {
      alert('Please enter at least one super-category');
      return;
    }

    const totalFlag = currentUnion.isTotal ? ' @total' : '';
    let unionNotation = `[Union]\n${currentUnion.subCategoryEntity}${totalFlag}\n--`;

    unionNotation += '\n';
    validSuperCategories.forEach(superCategory => {
      unionNotation += `${superCategory}\n`;
    });

    // Check if we're editing an existing component
    if (editingMode && editingMode.isEditing && editingMode.componentType === 'union' && editingMode.originalComponent) {
      const originalText = editingMode.originalComponent.originalText;
      let replaced = false;

      if (originalText && erdState.inputText.includes(originalText)) {
        erdState.setInputText(erdState.inputText.replace(originalText, unionNotation + '\n\n'));
        replaced = true;
      } else {
        // Fallback: match by name, first occurrence only
        const originalName = editingMode.originalComponent.name;
        const lines = erdState.inputText.split('\n');
        const newLines = [];
        let i = 0;
        while (i < lines.length) {
          const line = lines[i];
          if (!replaced && line.trim() === '[Union]' && i + 1 < lines.length) {
            const nextLine = lines[i + 1].trim();
            const nameMatch = nextLine.match(/^(\w+)/);
            if (nameMatch && nameMatch[1] === originalName) {
              newLines.push(...unionNotation.split('\n'));
              replaced = true;
              i += 2;
              while (i < lines.length && !lines[i].trim().startsWith('[')) {
                i++;
              }
              continue;
            }
          }
          newLines.push(line);
          i++;
        }
        if (replaced) {
          erdState.setInputText(newLines.join('\n'));
        } else {
          erdState.setInputText(prev => prev + (prev ? '\n\n' : '') + unionNotation);
        }
      }

      setPendingSaveEvent('union_form_submitted');

      // Clear editing mode
      setEditingMode({
        isEditing: false,
        componentType: null,
        originalComponent: null
      });
    } else {
      console.log('Adding new union (not editing)');
      // Add new component
      erdState.setInputText(prev => prev + (prev ? '\n\n' : '') + unionNotation);
      // Set pending save event for after state update
      setPendingSaveEvent('union_added');
    }

    // Reset form after successful addition/edit
    setCurrentUnion({
      subCategoryEntity: '',
      isTotal: false,
      superCategories: ['']
    });
  };

  // Effect to track inputText changes and trigger pending save events
  useEffect(() => {
    // Only trigger save if inputText actually changed and we have a pending event
    if (erdState.inputText !== inputTextRef.current && pendingSaveEvent && onSaveEvent) {
      inputTextRef.current = erdState.inputText;
      // Use setTimeout to ensure the state has fully propagated
      setTimeout(() => {
        onSaveEvent(pendingSaveEvent);
        setPendingSaveEvent(null);
      }, 0);
    } else {
      inputTextRef.current = erdState.inputText;
    }
  }, [erdState.inputText, pendingSaveEvent, onSaveEvent]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">
          {editingMode && editingMode.isEditing && editingMode.componentType === 'union'
            ? `Edit Union: ${editingMode.originalComponent?.name || 'Unknown'}`
            : 'Add Union'}
        </h2>
        <MdMergeType className="w-6 h-6 text-gray-600" />
      </div>

      {/* Sub-category Entity */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Sub-category Entity</label>
        <select
          value={currentUnion.subCategoryEntity}
          onChange={(e) => setCurrentUnion(prev => ({ ...prev, subCategoryEntity: e.target.value }))}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Select sub-category entity...</option>
          {erdState.availableEntities?.map((entity, idx) => (
            <option key={idx} value={entity}>{entity}</option>
          ))}
        </select>
      </div>

      {/* Total Union */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={currentUnion.isTotal || false}
            onChange={(e) => setCurrentUnion(prev => ({ ...prev, isTotal: e.target.checked }))}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Total Union</span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">Every instance of the super-categories participates in the union. Shown as double lines from the U node to each super-category.</p>
      </div>

      {/* Super-categories */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Super-categories ({currentUnion.superCategories.filter(category => category.trim() !== '').length})
          </label>
          <button
            onClick={addSuperCategory}
            className="text-blue-500 hover:text-blue-700 text-sm font-medium"
          >
            + Add Super-category
          </button>
        </div>

        <div className="space-y-4">
          {currentUnion.superCategories.map((superCategory, index) => {
            const isExpanded = expandedSuperCategories[index] !== false;

            return (
              <div key={index} className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden transition-all duration-200 mb-1">
                {/* Super-category Header */}
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                  onClick={() => toggleSuperCategoryExpansion(index)}
                >
                  <div className="flex items-center flex-1">
                    <span className="text-sm font-medium text-gray-700">
                      {superCategory.trim() !== '' ? superCategory : `Super-category ${index + 1}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {currentUnion.superCategories.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSuperCategory(index);
                        }}
                        className="text-red-500 hover:text-red-700 text-xs px-2 py-1 transition-colors duration-150"
                      >
                        Remove
                      </button>
                    )}
                    <div className="transition-transform duration-200">
                      {isExpanded ? (
                        <MdExpandLess className="w-4 h-4 text-gray-500" />
                      ) : (
                        <MdExpandMore className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Super-category Content */}
                <div className={`transition-all duration-200 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                  <div className="px-3 pb-3 pt-1 space-y-3">
                    <select
                      value={superCategory}
                      onChange={(e) => updateSuperCategory(index, e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 transition-all duration-150"
                    >
                      <option value="">Select super-category...</option>
                      {erdState.availableEntities?.map((entity, idx) => (
                        <option key={idx} value={entity}>{entity}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add/Edit Union Button */}
      <button
        onClick={generateUnionNotation}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-md font-medium transition-colors duration-200"
      >
        {editingMode && editingMode.isEditing && editingMode.componentType === 'union'
          ? 'Confirm Edits'
          : 'Add Union to Notation'}
      </button>
    </div>
  );
};

export default UnionForm;